import React, { useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { format, formatDistanceToNow } from 'date-fns';
import AuditLogDetailsModal from './AuditLogDetailsModal';

const ACTION_COLORS = {
    'SYSTEM_CONFIG_UPDATED': 'bg-blue-300 text-blue-600 border-blue-600/50',
    'CLIENT_MAINTENANCE_ENABLED': 'bg-orange-300 text-orange-600 border-orange-600/50',
    'CLIENT_MAINTENANCE_DISABLED': 'bg-green-300 text-green-600 border-green-600/50',
    'CACHE_CLEARED': 'bg-purple-300 text-purple-600 border-purple-600/50',
    'DATABASE_MIGRATIONS_RUN': 'bg-indigo-300 text-indigo-600 border-indigo-600/50',
    'CUSTOMER_IMPERSONATION': 'bg-yellow-300 text-yellow-600 border-yellow-600/50',
    'AUDIT_LOGS_CLEANUP': 'bg-red-300 text-red-600 border-red-600/50'
};

const AuditLogTable = ({ logs, pagination, onPageChange, loading }) => {
    const [selectedLog, setSelectedLog] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10; // Fixed rows per page

    const handleRowClick = (log) => {
        setSelectedLog(log);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedLog(null);
    };

    if (logs.length === 0 && !loading) {
        return (
            <div className="card-container rounded-md p-6">
                <div className="flex flex-col items-center justify-center gap-3 text-font-secondary">
                    <Icon icon="mdi:file-document-outline" className="text-5xl" />
                    <p className="text-lg">No audit logs found</p>
                    <p className="text-sm">Try adjusting your filters or date range</p>
                </div>
            </div>
        );
    }

    // Pagination data slicing
    const totalPages = Math.ceil(logs.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedLogs = logs.slice(startIndex, startIndex + rowsPerPage);

    return (
        <>
            <div className="flex flex-col gap-3">
                {/* Desktop Table Header */}
                <div className="hidden md:block">
                    <table className="w-full table-auto text-left border-collapse text-sm">
                        <thead>
                            <tr className="text-font-primary rounded-md h-[48px] card-container font-semibold">
                                <th className="w-[150px] truncate max-w-[150px] pl-4 pr-2">Admin</th>
                                <th className="w-[200px] truncate max-w-[200px] pr-2">Action</th>
                                <th className="w-[180px] truncate max-w-[180px] pr-2">Timestamp</th>
                                <th className="w-[120px] truncate max-w-[120px] pr-2">IP Address</th>
                                <th className="w-[60px] truncate max-w-[60px] pr-4">Details</th>
                            </tr>
                        </thead>
                    </table>
                </div>

                {/* Mobile Header */}
                <div className="md:hidden">
                    <div className="flex items-center justify-between px-3 py-2 text-font-primary text-sm h-[48px] card-container font-semibold">
                        <span className="font-normal">Audit Logs</span>
                        <span className="font-normal text-xs">
                            {paginatedLogs.length} displayed
                        </span>
                    </div>
                </div>

                <div>
                    {/* Desktop Table View */}
                    <table className="w-full table-auto text-left border-collapse text-sm hidden md:table">
                        <tbody>
                            {paginatedLogs.map((log, index) => {
                                return (
                                    <tr
                                        key={log.id}
                                        onClick={() => handleRowClick(log)}
                                        className="h-[56px] border-b border-border text-font-secondary hover:text-primary hover:bg-background cursor-pointer transition-all duration-400 ease-out-in"
                                    >
                                        <td className="w-[150px] truncate max-w-[150px] pl-4 pr-2">
                                            <div className="text-sm font-medium text-font-primary truncate">
                                                {log.admin_name || 'Unknown'}
                                            </div>
                                            <div className="text-xs text-font-secondary truncate">
                                                {log.admin_email || log.admin_user_id}
                                            </div>
                                        </td>
                                        <td className="w-[200px] truncate max-w-[200px] pr-2">
                                            <span className={`
                                                inline-flex items-center px-2 py-1.5 rounded text-xs font-bold border
                                                ${ACTION_COLORS[log.action_type] || 'bg-gray-500 text-gray-500 border-gray-500/50'} 
                                                bg-opacity-20
                                            `}>
                                                {log.action_type.replace(/_/g, ' ')}
                                            </span>
                                        </td>
                                        <td className="w-[180px] truncate max-w-[180px] pr-2">
                                            <div className="text-sm text-font-primary">
                                                {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                                            </div>
                                            <div className="text-xs text-font-secondary">
                                                {format(new Date(log.timestamp), 'MMM d, HH:mm:ss')}
                                            </div>
                                        </td>
                                        <td className="w-[120px] truncate max-w-[120px] pr-2">
                                            <div className="text-sm font-mono text-font-primary">
                                                {log.ip_address || 'N/A'}
                                            </div>
                                        </td>
                                        <td className="w-[60px] truncate max-w-[60px] pr-4">
                                            <button className="text-primary hover:text-secondary transition-colors">
                                                <Icon icon="mdi:eye" className="text-xl" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {/* Mobile Card View */}
                    <div className="md:hidden flex flex-col gap-2">
                        {paginatedLogs.map((log, index) => {
                            return (
                                <div
                                    key={log.id}
                                    onClick={() => handleRowClick(log)}
                                    className="p-3 rounded-lg cursor-pointer transition-all duration-300 ease-out-in hover:shadow-lg hover:scale-[1.02] bg-background shadow-button border border-border"
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <span className={`
                                            inline-flex items-center px-2 py-1 rounded text-xs font-medium
                                            ${ACTION_COLORS[log.action_type] || 'bg-gray-500'} 
                                            bg-opacity-20 text-font-primary
                                        `}>
                                            {log.action_type.replace(/_/g, ' ')}
                                        </span>
                                        <span className="text-xs text-font-secondary">
                                            {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                                        </span>
                                    </div>

                                    <div className="flex flex-col gap-1 mb-2">
                                        <span className="text-sm font-medium text-font-primary">
                                            {log.admin_name || 'Unknown'}
                                        </span>
                                        <span className="text-xs text-font-secondary truncate">
                                            {log.admin_email || log.admin_user_id}
                                        </span>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-font-secondary font-mono">
                                            {log.ip_address || 'N/A'}
                                        </span>
                                        <Icon icon="mdi:eye" className="text-lg text-primary" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center justify-between md:px-4 py-3 border-t border-border text-sm">
                    <div>
                        <span className="text-font-secondary">
                            Showing <span className="font-semibold text-primary">{Math.min(startIndex + 1, logs.length)}</span> to <span className="font-semibold text-primary">{Math.min(startIndex + rowsPerPage, logs.length)}</span> of <span className="font-semibold text-primary">{logs.length}</span> results
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                            className="p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                        >
                            <Icon icon="icon-park-outline:double-left" className="text-xl text-accent" />
                        </button>
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                        >
                            <Icon icon="material-symbols:arrow-back-ios-new" className="text-lg text-accent" />
                        </button>
                        <span className="text-font-secondary">
                            <span className="font-bold text-accent">{currentPage}</span> of <span className="font-bold">{totalPages}</span>
                        </span>
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                        >
                            <Icon icon="material-symbols:arrow-forward-ios" className="text-lg text-accent" />
                        </button>
                        <button
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                            className="p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                        >
                            <Icon icon="icon-park-outline:double-right" className="text-xl text-accent" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Details Modal */}
            {isModalOpen && selectedLog && (
                <AuditLogDetailsModal log={selectedLog} onClose={closeModal} />
            )}
        </>
    );
};

export default AuditLogTable;

